import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import NewSidebar from './NewSidebar';
import { isSuperAdmin } from '../utils/permissions';
import { useAccount } from '../contexts/AccountContext';

// Default position for Classic UI toggle button
const DEFAULT_BUTTON_POSITION = { x: null, y: 16 }; // null means right-aligned

function NewLayout({
  user,
  onLogout,
  onChatSelect,
  onNewChat,
  chatHistoryVersion,
  selectedChat,
  readOnly,
  onEndImpersonation,
  children
}) {
  const navigate = useNavigate();
  const { getAccountIdToUse } = useAccount();
  const accountId = getAccountIdToUse();
  const isUISwitchingEnabled = isSuperAdmin(user);

  // State to track which UI experience to use
  const [useNewExperience, setUseNewExperience] = useState(() => {
    // Check localStorage for user preference
    const saved = localStorage.getItem('useNewExperience');
    // Default to new experience (true) if no saved preference
    return saved !== null ? saved === 'true' : true;
  });

  // State to track if new sidebar is collapsed
  const [isNewSidebarCollapsed, setIsNewSidebarCollapsed] = useState(() => {
    // Check localStorage for collapse state
    const saved = localStorage.getItem('newSidebarCollapsed');
    return saved === 'true';
  });

  // State for draggable toggle button position
  const [buttonPosition, setButtonPosition] = useState(() => {
    const saved = localStorage.getItem('classicUIButtonPosition');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return DEFAULT_BUTTON_POSITION;
      }
    }
    return DEFAULT_BUTTON_POSITION;
  });

  // ── Refresh the routed page when the account admin switches account ──────
  // Every page fetches with the selected account id, but a page's STATE does
  // not follow the switch: already-fetched rows, expanded rows, selections, and
  // refs that latch a one-time load (the Triggers page's `autoRan` suppresses
  // its recommendation reload for the component's lifetime). So switching
  // accounts left the previous account's data on screen next to the new
  // account's name.
  //
  // Remounting the content subtree makes every page re-run its fetches and drop
  // its old state, without each page having to opt in one at a time.
  //
  // The key is an epoch rather than the account id itself: the account context
  // resolves the id in a mount effect, so the FIRST render of every page always
  // has none, and keying on the id would remount (double-mount) every page on
  // load. The epoch only advances on a real account → different account switch.
  const [accountEpoch, setAccountEpoch] = useState(0);
  const mountedAccountRef = useRef(accountId || null);
  useEffect(() => {
    if (!accountId) return;                                  // not resolved yet
    if (mountedAccountRef.current === accountId) return;      // same account
    const previous = mountedAccountRef.current;
    mountedAccountRef.current = accountId;
    if (previous) setAccountEpoch((e) => e + 1);              // a switch, not the first resolve
  }, [accountId]);

  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef(null);
  const dragStartRef = useRef({ x: 0, y: 0, buttonX: 0, buttonY: 0 });
  const hasDraggedRef = useRef(false); // Track if actual dragging occurred

  // Only Super Admins can retain a Classic UI preference.
  useEffect(() => {
    if (!isUISwitchingEnabled) {
      setUseNewExperience(true);
    }
  }, [isUISwitchingEnabled]);

  // Save preference to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('useNewExperience', useNewExperience.toString());
  }, [useNewExperience]);

  // Save collapse state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('newSidebarCollapsed', isNewSidebarCollapsed.toString());
  }, [isNewSidebarCollapsed]);

  // Save button position to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('classicUIButtonPosition', JSON.stringify(buttonPosition));
  }, [buttonPosition]);

  // Drag handlers for the toggle button
  const handleDragStart = useCallback((e) => {
    e.preventDefault();
    hasDraggedRef.current = false; // Reset on mouse down
    setIsDragging(true);
    const rect = dragRef.current.getBoundingClientRect();
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      buttonX: rect.left,
      buttonY: rect.top
    };
  }, []);

  const handleDragMove = useCallback((e) => {
    if (!isDragging) return;

    const deltaX = e.clientX - dragStartRef.current.x;
    const deltaY = e.clientY - dragStartRef.current.y;

    // Only consider it a drag if moved more than 5 pixels
    if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
      hasDraggedRef.current = true;
    }

    let newX = dragStartRef.current.buttonX + deltaX;
    let newY = dragStartRef.current.buttonY + deltaY;

    // Keep button within viewport bounds
    const buttonWidth = dragRef.current?.offsetWidth || 120;
    const buttonHeight = dragRef.current?.offsetHeight || 40;

    newX = Math.max(0, Math.min(window.innerWidth - buttonWidth, newX));
    newY = Math.max(0, Math.min(window.innerHeight - buttonHeight, newY));

    setButtonPosition({ x: newX, y: newY });
  }, [isDragging]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add/remove global mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleDragMove);
        window.removeEventListener('mouseup', handleDragEnd);
      };
    }
  }, [isDragging, handleDragMove, handleDragEnd]);

  const handleToggleExperience = () => {
    if (!isUISwitchingEnabled) return;

    const newExperienceValue = !useNewExperience;
    setUseNewExperience(newExperienceValue);

    // Update localStorage immediately before dispatching event
    localStorage.setItem('useNewExperience', newExperienceValue.toString());

    // Dispatch custom event to notify other components immediately
    window.dispatchEvent(new CustomEvent('uiExperienceChanged'));

    navigate('/');
  };

  const handleToggleNewSidebarCollapse = () => {
    setIsNewSidebarCollapsed(!isNewSidebarCollapsed);
  };

  return (
    <div className="h-screen bg-white flex overflow-hidden">
      {/* Experience Toggle Button - draggable and persistent */}
      {/* UI switching is available only to Super Admins. */}
      {isUISwitchingEnabled && (
        <div
          ref={dragRef}
          className="fixed z-50"
          style={{
            // If x is null, position from right edge; otherwise use x coordinate
            ...(buttonPosition.x === null
              ? { right: '16px', top: `${buttonPosition.y}px` }
              : { left: `${buttonPosition.x}px`, top: `${buttonPosition.y}px` }),
            cursor: isDragging ? 'grabbing' : 'grab',
            userSelect: 'none'
          }}
        >
          <button
            onMouseDown={handleDragStart}
            onClick={() => {
              // Only toggle if not actually dragged (allow click without drag)
              if (!hasDraggedRef.current) {
                handleToggleExperience();
              }
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow-lg transition-colors duration-200 text-sm font-medium"
            title={`Switch to ${useNewExperience ? 'Classic' : 'New'} Experience (drag to reposition)`}
          >
            {useNewExperience ? '🔄 Classic UI' : '✨ New UI'}
          </button>
        </div>
      )}

      {/* Render appropriate sidebar based on experience */}
      {useNewExperience ? (
        <NewSidebar
          user={user}
          onLogout={onLogout}
          onChatSelect={onChatSelect}
          onNewChat={onNewChat}
          chatHistoryVersion={chatHistoryVersion}
          selectedChat={selectedChat}
          readOnly={readOnly}
          onEndImpersonation={onEndImpersonation}
          isCollapsed={isNewSidebarCollapsed}
          onToggleCollapse={handleToggleNewSidebarCollapse}
        />
      ) : (
        <Sidebar
          user={user}
          onLogout={onLogout}
          onChatSelect={onChatSelect}
          onNewChat={onNewChat}
          chatHistoryVersion={chatHistoryVersion}
          selectedChat={selectedChat}
          readOnly={readOnly}
          onEndImpersonation={onEndImpersonation}
        />
      )}

      {/* Main content area. Keyed by accountEpoch so an account switch remounts
          whatever page is showing — see the epoch comment above. */}
      <div
        key={accountEpoch}
        className={`flex-1 overflow-auto transition-all duration-300 ${
          useNewExperience && isNewSidebarCollapsed
            ? 'ml-32' // 120px (80px sidebar + 20px left margin + 20px right spacing)
            : useNewExperience
              ? 'ml-0' // No margin when expanded (sidebar is in normal flow)
              : 'ml-0' // Classic sidebar handles its own spacing
        }`}
      >
        {children}
      </div>
    </div>
  );
}

export default NewLayout;
